/**
 * widgetTokens.ts
 *
 * Single source of truth for Tailwind class strings shared across all widget
 * components. Import `wt` in each widget instead of Widget.css.
 *
 * Any change here applies to every widget instantly — no hunting through CSS.
 */

export const wt = {
  // ── Layout ──────────────────────────────────────────────────────
  container: 'flex flex-col gap-1.5',
  labelRow:  'flex items-center justify-between gap-2',

  // ── Labels ──────────────────────────────────────────────────────
  label:       'text-label font-medium text-gray-700',
  required:    'ml-0.5 text-error',
  description: 'text-meta text-gray-500 leading-snug',
  errorMsg:    'text-meta text-error',
  hint:        'text-meta text-gray-500',

  // ── Shared input base (text, textarea, select, number) ──────────
  inputBase: [
    'w-full rounded-md border border-gray-300 bg-white',
    'px-3 py-2.5 text-body text-gray-900',
    'placeholder:text-gray-400',
    'outline-none transition-[border-color,box-shadow] duration-base',
    'focus:border-blue-400 focus:shadow-focus',
    'disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400',
  ].join(' '),

  get input()      { return this.inputBase; },
  get textarea()   { return `${this.inputBase} resize-y min-h-[90px]`; },
  get select()     { return `${this.inputBase} select-input cursor-pointer`; },
  get numberInput(){ return `${this.inputBase} [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`; },

  inputError: 'border-error focus:shadow-focus-error',

  // ── Number widget stepper buttons ────────────────────────────────
  stepperWrap: 'flex items-center gap-0',
  stepperBtn: [
    'flex items-center justify-center w-9 h-10',
    'bg-gray-100 border border-gray-300 text-gray-600',
    'text-base font-medium leading-none',
    'transition-colors duration-fast',
    'hover:bg-gray-200 hover:text-gray-800',
    'disabled:opacity-40 disabled:cursor-not-allowed',
    'first:rounded-l-md last:rounded-r-md',
  ].join(' '),
  stepperInput: [
    'flex-1 text-center border-y border-gray-300 bg-white h-10',
    'text-body text-gray-900 outline-none',
    'focus:border-blue-400 focus:shadow-focus',
    'disabled:bg-gray-100 disabled:text-gray-400',
    '[appearance:textfield]',
    '[&::-webkit-inner-spin-button]:appearance-none',
    '[&::-webkit-outer-spin-button]:appearance-none',
  ].join(' '),

  // ── Seed widget ──────────────────────────────────────────────────
  seedWrap:  'flex gap-2',
  seedInput: [
    'flex-1 rounded-md border border-gray-300 bg-white',
    'px-3 py-2.5 text-body text-gray-900 font-mono',
    'outline-none transition-[border-color,box-shadow] duration-base',
    'focus:border-blue-400 focus:shadow-focus',
    'disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed',
    '[appearance:textfield]',
    '[&::-webkit-inner-spin-button]:appearance-none',
    '[&::-webkit-outer-spin-button]:appearance-none',
  ].join(' '),
  seedBtn: [
    'flex items-center gap-1.5 px-3 py-2.5 rounded-md',
    'border border-gray-300 bg-gray-50 text-gray-600',
    'text-label font-medium whitespace-nowrap',
    'transition-colors duration-fast',
    'hover:bg-gray-100 hover:border-gray-400 hover:text-gray-800',
    'disabled:opacity-40 disabled:cursor-not-allowed',
  ].join(' '),

  // ── Slider widget ────────────────────────────────────────────────
  sliderWrap:    'flex flex-col gap-2',
  sliderTrack:   'slider-input',   // pseudo-elements handled in index.css
  sliderMeta:    'flex justify-between items-center',
  sliderValue:   'text-label font-semibold text-blue-500',
  sliderMinMax:  'text-meta text-gray-400',

  // ── Switch widget ────────────────────────────────────────────────
  switchRow:     'flex items-center justify-between gap-4 py-1',
  switchLabel:   'text-label font-medium text-gray-700 select-none cursor-pointer',
  switchTrack: (on: boolean) => [
    'relative inline-flex h-6 w-11 items-center rounded-full cursor-pointer',
    'transition-colors duration-base outline-none',
    on ? 'bg-blue-500' : 'bg-gray-300',
  ].join(' '),
  switchThumb: (on: boolean) => [
    'inline-block h-4 w-4 rounded-full bg-white shadow-sm',
    'transition-transform duration-base',
    on ? 'translate-x-6' : 'translate-x-1',
  ].join(' '),

  // ── Tooltip ──────────────────────────────────────────────────────
  tooltipTrigger: 'flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-[10px] font-bold cursor-help hover:bg-gray-300 transition-colors duration-fast',
  tooltipBox: [
    'absolute z-50 w-56 p-3 rounded-lg bg-gray-900 text-white text-meta leading-relaxed',
    'shadow-lg tooltip-animate',
    'bottom-full right-0 mb-2',
  ].join(' '),
  tooltipWrap: 'relative',
} as const;
